/**
 * Acquisition attribution capture — first-touch AND last-touch.
 *
 * First-touch:  Stashed once in localStorage under `simonara_attribution`.
 *               Never overwritten on later visits (mirrors the server-side
 *               `first-touch-wins` policy in routers/auth.py). The register()
 *               flow in AuthContext.jsx picks this up and forwards it to
 *               POST /auth/register where it lands on
 *               users.attribution_first_touch.
 *
 * Last-touch:   Fired every time the SPA boots (or the user logs in) while a
 *               UTM param is present in the URL AND a session token exists.
 *               Lands on users.attribution_last_touch via
 *               POST /api/attribution/touch. This is what powers the
 *               "which channel closed the returning visitor" side of the
 *               attribution dashboard.
 */
const STORAGE_KEY = "simonara_attribution";

function _readUrlAttribution() {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const stamped = {};
    for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
        const v = params.get(k);
        if (v) stamped[k] = v.slice(0, 160);
    }
    stamped.landing_page = (window.location.pathname + window.location.search).slice(0, 500);
    if (document.referrer) stamped.referrer = document.referrer.slice(0, 500);
    const anyUtm = Object.keys(stamped).some((k) => k.startsWith("utm_"));
    const offSiteReferrer = stamped.referrer && !stamped.referrer.includes(window.location.host);
    if (!anyUtm && !offSiteReferrer) return null;
    return stamped;
}

export function captureFirstTouchAttribution() {
    if (typeof window === "undefined") return;
    try {
        const stamped = _readUrlAttribution();
        // Persist first-touch to localStorage once. Never overwrite.
        if (stamped && !window.localStorage.getItem(STORAGE_KEY)) {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stamped));
        }
        // Fire last-touch on EVERY boot with UTMs — even when first-touch is
        // already stashed. Otherwise return-visits from a re-marketing link
        // (e.g. `/?utm_source=twitter`) would never hit
        // `attribution_last_touch` on the user row.
        if (stamped) fireLastTouch(stamped);
    } catch {
        /* ignore */
    }
}

/**
 * Fire /api/attribution/touch with the given UTM payload (or from URL when
 * none supplied). Safe no-op when there is no session token or no UTM signal.
 * Exposed so AuthContext can call it right after login — when the user landed
 * on `/?utm_source=twitter` first, then signed in.
 */
export function fireLastTouch(payload) {
    if (typeof window === "undefined") return;
    try {
        const token = window.localStorage.getItem("simonara_user_token");
        if (!token) return;
        const stamped = payload || _readUrlAttribution();
        if (!stamped) return;
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/attribution/touch`;
        fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json", "X-User-Token": token },
            body: JSON.stringify(stamped),
            keepalive: true,
        }).catch(() => { /* silent */ });
    } catch { /* silent */ }
}
